import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

const looks = [
  {
    id: 1,
    before: "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1788960884/n8n_uploads/vzfvwlghnmr79dfw1gh6.jpg",
    after: "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1788960918/rhsmfssctwa7xf9vgfkq.jpg",
    title: "trench_camel_v2",
    engine: "SD 4.5",
  },
  {
    id: 2,
    before: "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1788971928/n8n_uploads/gernt5rcyjsb3kgu3trg.jpg",
    after: "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1788971999/fmdzf9sxblf27moz9vyx.png",
    title: "silk_knit_ivory",
    engine: "SD 5 Pro",
  },
  {
    id: 3,
    before: "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1788960526/n8n_uploads/ahkzupumy2uehkfpgyhb.jpg",
    after: "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1788960587/acxmebsay90h2vecp02a.png",
    title: "denim_raw_indigo",
    engine: "Nano Banana Pro",
  },
];

function Comparator({ before, after, title, engine, index }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);
  const hasInteracted = useRef(false);

  // On mount, animate the divider from 65% → 35% → 50% once, to signal it's draggable.
  useEffect(() => {
    let cancelled = false;
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));
    (async () => {
      await wait(600 + index * 250);
      if (cancelled || hasInteracted.current) return;
      const path = [50, 68, 32, 50];
      for (const p of path) {
        if (cancelled || hasInteracted.current) return;
        setPos(p);
        await wait(700);
      }
    })();
    return () => { cancelled = true; };
  }, [index]);

  const onMove = (clientX) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    let p = ((clientX - r.left) / r.width) * 100;
    p = Math.max(4, Math.min(96, p));
    hasInteracted.current = true;
    setPos(p);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.9, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      className="relative"
      data-testid={`compare-${index}`}
    >
      <div className="flex items-center justify-between mb-3 font-mono text-[10px] uppercase tracking-[0.22em] text-[rgb(var(--paper-dim))]">
        <span>#{String(index + 1).padStart(3, "0")}</span>
        <span>{engine}</span>
      </div>
      <div
        ref={ref}
        onPointerDown={(e) => { dragging.current = true; hasInteracted.current = true; onMove(e.clientX); e.currentTarget.setPointerCapture(e.pointerId); }}
        onPointerMove={(e) => { if (dragging.current) onMove(e.clientX); }}
        onPointerUp={() => { dragging.current = false; }}
        className="relative aspect-[3/4] rounded-xl overflow-hidden border border-white/10 select-none cursor-ew-resize bg-[rgb(var(--ink-2))]"
      >
        {/* After (base layer) */}
        <img src={after} alt="" draggable={false} className="absolute inset-0 w-full h-full object-cover" />
        {/* Before (clipped from left) */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)`, transition: "clip-path 700ms cubic-bezier(0.16,1,0.3,1)" }}
        >
          <img src={before} alt="" draggable={false} className="absolute inset-0 w-full h-full object-cover" />
        </div>

        {/* HUD corners */}
        <span className="absolute top-2.5 left-2.5 h-2.5 w-2.5 border-l border-t border-[rgb(var(--brand))]/70" />
        <span className="absolute top-2.5 right-2.5 h-2.5 w-2.5 border-r border-t border-[rgb(var(--brand))]/70" />
        <span className="absolute bottom-2.5 left-2.5 h-2.5 w-2.5 border-l border-b border-[rgb(var(--brand))]/70" />
        <span className="absolute bottom-2.5 right-2.5 h-2.5 w-2.5 border-r border-b border-[rgb(var(--brand))]/70" />

        {/* labels */}
        <div className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.22em] text-white bg-black/60 backdrop-blur px-2 py-0.5 rounded">
          Antes
        </div>
        <div className="absolute top-3 right-3 font-mono text-[10px] uppercase tracking-[0.22em] text-[rgb(var(--ink))] bg-[rgb(var(--brand))] px-2 py-0.5 rounded">
          Después
        </div>

        {/* divider */}
        <div
          className="absolute inset-y-0 pointer-events-none"
          style={{ left: `${pos}%`, transition: "left 700ms cubic-bezier(0.16,1,0.3,1)" }}
        >
          <div className="w-[3px] h-full bg-[rgb(var(--brand))] glow-cyan -translate-x-1/2" />
          <div className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-1/2 h-11 w-11 rounded-full bg-[rgb(var(--brand))] text-[rgb(var(--ink))] grid place-items-center font-mono font-bold text-base shadow-[0_0_28px_rgba(92,225,230,0.6)]">
            ⇔
          </div>
        </div>

        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-end font-mono text-[11px] text-white">
          <span className="bg-black/50 backdrop-blur px-2.5 py-1 rounded text-[rgb(var(--brand))]">4K</span>
        </div>
      </div>
    </motion.div>
  );
}

export function Trabajo() {
  return (
    <section id="trabajo" className="relative px-5 md:px-8 py-24 md:py-32">
      <div className="max-w-[1500px] mx-auto">
        <div className="flex items-end justify-between mb-14 md:mb-16 gap-6 flex-wrap">
          <div>
            <span className="tag mb-4 inline-flex">§ 003 · Ejemplos · Arrastra para comparar</span>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="font-display text-[8vw] md:text-[4.6vw] leading-[0.95] tracking-[-0.02em] text-[rgb(var(--paper))] max-w-3xl font-light"
            >
              De la prenda <span className="italic font-serif text-[rgb(var(--brand))]">a la campaña.</span>
            </motion.h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7">
          {looks.map((l, i) => (
            <Comparator key={l.id} {...l} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
